import { useState, useMemo, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { steelChromeButton, searchStyles } from "@/lib/status-styles.js";

/**
 * Column dialog: add or edit a measurement column by choosing a universal descriptor.
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onOpenChange
 * @param {Array<{ universalDescriptorId: number, UD: string }>} props.descriptorPickerItems
 * @param {Map<number, number>} [props.descriptorAssetCounts]
 * @param {boolean} [props.isLoadingDescriptors]
 * @param {Array} props.existingColumns
 * @param {Object} [props.column] - Edit mode: column to edit
 * @param {Function} props.onAdd - `{ displayName, universalDescriptorId }`
 * @param {Function} [props.onUpdate] - same shape
 * @param {Function} [props.onDelete]
 * @param {string} [props.pickerSectionTitle] - Label above the descriptor list
 */
export default function ColumnDialog({
  open,
  onOpenChange,
  descriptorPickerItems = [],
  descriptorAssetCounts = new Map(),
  isLoadingDescriptors = false,
  existingColumns = [],
  column = null,
  onAdd,
  onUpdate,
  onDelete,
  pickerSectionTitle,
}) {
  const isEditMode = !!column;
  const [displayName, setDisplayName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUdId, setSelectedUdId] = useState(
    /** @type {number|null} */ (null)
  );

  useEffect(() => {
    if (isEditMode) {
      if (open && column) {
        setDisplayName(column.labelName || "");
        setSearchTerm("");
        setSelectedUdId(column.universalDescriptorId);
      }
    } else if (!open) {
      setDisplayName("");
      setSearchTerm("");
      setSelectedUdId(null);
    }
  }, [open, column, isEditMode]);

  useEffect(() => {
    if (selectedUdId != null && !displayName.trim()) {
      const row = descriptorPickerItems.find(
        (d) => d.universalDescriptorId === selectedUdId
      );
      setDisplayName(row?.UD ? String(row.UD) : String(selectedUdId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUdId]);

  const filteredDescriptors = useMemo(() => {
    let list = descriptorPickerItems;
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      list = descriptorPickerItems.filter((row) => {
        const ud = String(row.UD || "").toLowerCase();
        const idStr = String(row.universalDescriptorId);
        return ud.includes(s) || idStr.includes(s);
      });
    }
    return [...list].sort((a, b) => {
      const ca = descriptorAssetCounts.get(a.universalDescriptorId) || 0;
      const cb = descriptorAssetCounts.get(b.universalDescriptorId) || 0;
      if (ca !== cb) return cb - ca;
      return String(a.UD).localeCompare(String(b.UD));
    });
  }, [descriptorPickerItems, searchTerm, descriptorAssetCounts]);

  const handleUdSelect = useCallback((id) => {
    setSelectedUdId((prev) => (prev === id ? null : id));
  }, []);

  const descriptorPickerRows = useMemo(() => {
    return filteredDescriptors.map((row) => {
      const id = row.universalDescriptorId;
      const isSelected = selectedUdId === id;
      return (
        <div
          key={id}
          onClick={() => handleUdSelect(id)}
          className={cn(
            "flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer transition-colors",
            isSelected && "bg-primary/10"
          )}>
          <div
            className={cn(
              "w-4 h-4 border-2 rounded-full flex items-center justify-center transition-colors",
              isSelected
                ? "border-primary bg-primary"
                : "border-border"
            )}>
            {isSelected && <div className='w-2 h-2 rounded-full bg-white' />}
          </div>
          <span className='text-sm text-foreground flex-1 min-w-0 truncate'>
            {row.UD}
          </span>
          {descriptorAssetCounts.has(id) && (
            <span className='text-xs text-muted-foreground shrink-0'>
              ({descriptorAssetCounts.get(id)})
            </span>
          )}
        </div>
      );
    });
  }, [
    filteredDescriptors,
    selectedUdId,
    descriptorAssetCounts,
    handleUdSelect,
  ]);

  const isDisplayNameUnique = useMemo(() => {
    if (!displayName.trim()) return true;
    const displayNameTrimmed = displayName.trim();
    return !existingColumns.some(
      (col) =>
        (isEditMode ? col.key !== column?.key : true)
        && col.labelName === displayNameTrimmed
    );
  }, [displayName, existingColumns, column, isEditMode]);

  const listLoading = isLoadingDescriptors;
  const listEmpty = filteredDescriptors.length === 0;

  const pickerLabel =
    pickerSectionTitle
    ?? `Select a Universal Descriptor (${descriptorPickerItems.length})`;

  const hasSelection = selectedUdId != null;

  const handleSave = () => {
    if (!displayName.trim() || !hasSelection || !isDisplayNameUnique) return;

    const data = {
      displayName: displayName.trim(),
      universalDescriptorId: /** @type {number} */ (selectedUdId),
    };

    if (isEditMode) {
      onUpdate(data);
      setDisplayName("");
      setSearchTerm("");
      setSelectedUdId(null);
      onOpenChange(false);
    } else {
      onOpenChange(false);
      setTimeout(() => {
        onAdd(data);
      }, 0);
    }
  };

  const handleDelete = () => {
    onDelete();
    setDisplayName("");
    setSearchTerm("");
    setSelectedUdId(null);
    onOpenChange(false);
  };

  const handleClose = () => {
    if (!isEditMode) {
      setDisplayName("");
      setSearchTerm("");
      setSelectedUdId(null);
    }
    onOpenChange(false);
  };

  if (isEditMode && !column) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg max-h-[80vh] flex flex-col'>
        <DialogHeader>
          <DialogTitle className='text-lg font-semibold text-foreground'>
            {isEditMode ? "Edit Column" : "Add Column"}
          </DialogTitle>
        </DialogHeader>
        <div className='space-y-6 py-4 flex-1 overflow-hidden flex flex-col'>
          <div className='space-y-2'>
            <label
              htmlFor='display-name'
              className='text-sm font-medium text-foreground'>
              Display Name
            </label>
            <Input
              id='display-name'
              type='text'
              placeholder='e.g., Suction Pressure'
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className={cn(
                "w-full",
                displayName.trim()
                  && !isDisplayNameUnique
                  && "border-red-500 focus:border-red-500 focus:ring-red-500"
              )}
              autoFocus
            />
            {displayName.trim() && !isDisplayNameUnique && (
              <p className='text-sm text-red-600'>
                Display name must be unique. This name is already in use.
              </p>
            )}
          </div>

          <div className='space-y-2 flex-1 flex flex-col min-h-0'>
            <label className='text-sm font-medium text-foreground'>
              {pickerLabel}
            </label>
            <div className='relative'>
              <Input
                type='text'
                placeholder='Search descriptors or IDs...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='w-full pr-10'
              />
              <Search className={searchStyles.icon} />
            </div>
            <div className='border border-border rounded-md overflow-y-auto flex-1 min-h-0 bg-card'>
              {listLoading ? (
                <div className='p-8 text-center'>
                  <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary' />
                  <p className='mt-4 text-sm text-muted-foreground'>
                    Loading universal descriptors...
                  </p>
                </div>
              ) : listEmpty ? (
                <div className='p-4 text-center text-sm text-muted-foreground'>
                  {searchTerm
                    ? "No matching descriptors"
                    : "No universal descriptors available"}
                </div>
              ) : (
                <div className='p-2 space-y-1'>{descriptorPickerRows}</div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter className='flex flex-row justify-end gap-2'>
          <Button
            onClick={handleSave}
            disabled={
              listLoading
              || !displayName.trim()
              || !hasSelection
              || !isDisplayNameUnique
            }
            className={cn(
              "disabled:opacity-50 disabled:cursor-not-allowed",
              isEditMode
                ? "font-normal text-sm bg-green-600 text-white hover:bg-green-700"
                : steelChromeButton
            )}>
            {isEditMode ? "Save" : "Add"}
          </Button>
          {isEditMode && (
            <Button
              variant='outline'
              onClick={handleDelete}
              disabled={listLoading}
              className='font-normal text-sm border-red-600 text-red-600 hover:bg-red-50 hover:text-red-700'>
              Delete
            </Button>
          )}
          <Button
            variant='outline'
            onClick={handleClose}
            className='h-9 font-normal text-sm border-border bg-background text-foreground hover:bg-muted shadow-none'>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
